import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { Message } from '@/types/messages.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

/** The worst finding's severity; a review without findings is `ok`. */
export type ReviewSeverity = 'critical' | 'high' | 'medium' | 'low' | 'ok';

/** The ways the reviewer names a user getting stuck or unhappy with Wundo. */
export type FindingClass =
  | 'said_it_did_not_help'
  | 'corrected_wundo'
  | 'repeated_request'
  | 'gave_up'
  | 'asked_for_a_human'
  | 'wundo_could_not_help'
  | 'unsupported_request';

export interface ReviewFinding {
  /** A `FindingClass`, or a class from before the reviewer asked about the user's reaction. */
  class: string;
  description: string;
  evidence: string;
  /** The transcript time of the line the evidence is from; findings from before it was asked for have none. */
  quoted_at?: string | null;
}

/**
 * A finding nobody has handled yet, with the severity its class carries. A
 * string, so a rung this console does not know yet still arrives and is left out.
 */
export interface OpenFinding extends ReviewFinding {
  severity: string;
}

/** An open review of one conversation: the span of messages it read, and its open findings. */
export interface OpenReview {
  id: string;
  first_message_id: number;
  last_message_id: number;
  /** The zone the reviewer's transcript printed its times in. */
  user_timezone: string;
  findings: OpenFinding[];
}

/** Which reviews the list shows by whether somebody has dealt with them. */
export type ReviewHandledFilter = 'open' | 'handled' | 'all';

/** One reviewed conversation. The names and the title are joined in by the API. */
export interface ConversationReview {
  id: string;
  chat_session_id: string;
  user_id: string;
  user_name: string | null;
  /** The zone the reviewer's transcript printed its times in. */
  user_timezone: string;
  organisation_name: string | null;
  session_title: string | null;
  severity: ReviewSeverity | null;
  findings: ReviewFinding[];
  handled_at: string | null;
  handled_by_name: string | null;
  trace_id: string | null;
  created_at: string;
}

/** What the list and the counts are both narrowed by. */
export interface ReviewFilters {
  severity?: ReviewSeverity[];
  class?: FindingClass[];
  handled?: ReviewHandledFilter;
  /** An ISO timestamp; only reviews written after it are counted or listed. */
  since?: string;
  organisation_ids?: string[];
}

function toParams(filters: ReviewFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.severity?.length) params.set('severity', filters.severity.join(','));
  if (filters.class?.length) params.set('class', filters.class.join(','));
  if (filters.handled) params.set('handled', filters.handled);
  if (filters.since) params.set('since', filters.since);
  if (filters.organisation_ids?.length) {
    params.set('organisation_ids', filters.organisation_ids.join(','));
  }
  return params;
}

export function fetchReviews(
  filters: ReviewFilters,
  page = 1,
  limit: number = PAGE_SIZE,
): Promise<PaginatedResponse<ConversationReview>> {
  const params = toParams(filters);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return apiFetchJson<PaginatedResponse<ConversationReview>>(
    `/conversation-reviews?${params.toString()}`,
  );
}

/** How many reviews of each severity the other filters match; the chips need every rung. */
export function fetchReviewCounts(
  filters: ReviewFilters,
): Promise<Record<ReviewSeverity, number>> {
  const query = toParams({ ...filters, severity: undefined }).toString();
  return apiFetchJson<{ data: Record<ReviewSeverity, number> }>(
    query ? `/conversation-reviews/counts?${query}` : '/conversation-reviews/counts',
  ).then((response) => response.data);
}

export function fetchReview(id: string): Promise<ConversationReview> {
  return apiFetchJson<{ data: ConversationReview }>(`/conversation-reviews/${id}`).then(
    (response) => response.data,
  );
}

/** The reviewed session's own messages, oldest first. */
export function fetchReviewMessages(id: string): Promise<Message[]> {
  return apiFetchJson<{ data: Message[] }>(`/conversation-reviews/${id}/messages`).then(
    (response) => response.data,
  );
}

export async function markReviewHandled(id: string): Promise<void> {
  await apiFetch(`/conversation-reviews/${id}/actions/handle`, { method: 'POST' });
}

export async function reopenReview(id: string): Promise<void> {
  await apiFetch(`/conversation-reviews/${id}/actions/unhandle`, { method: 'POST' });
}
